(() => {
  const formatMoney = (cents, format) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') return window.Shopify.formatMoney(cents, format);
    const template = format || '£{{amount}}';
    const token = template.match(/\{\{\s*(amount(?:_no_decimals)?(?:_with_comma_separator|_with_space_separator|_with_apostrophe_separator)?)\s*\}\}/);
    if (!token) return (Number(cents || 0) / 100).toFixed(2);
    const noDecimals = token[1].includes('no_decimals');
    const comma = token[1].includes('comma_separator');
    const space = token[1].includes('space_separator');
    const apostrophe = token[1].includes('apostrophe_separator');
    const decimal = comma ? ',' : '.';
    const thousands = comma ? '.' : space ? ' ' : apostrophe ? "'" : ',';
    const raw = (Number(cents || 0) / 100).toFixed(noDecimals ? 0 : 2).split('.');
    raw[0] = raw[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const value = raw.length > 1 ? `${raw[0]}${decimal}${raw[1]}` : raw[0];
    return template.replace(token[0], value);
  };

  class WholesaleRecommendations extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-recommendations-content]');
      this.exploreContextUrl = this.content?.dataset.recExploreUrl || '';
      this.modal = this.querySelector('[data-rec-modal]');
      this.modalContent = this.querySelector('[data-rec-modal-content]');
      this.clickHandler = event => this.handleClick(event);
      this.inputHandler = event => {
        if (event.target.matches('[data-modal-quantity]')) {
          this.normalizeQuantity(event.target, 0);
          this.calculateModal();
        }
      };
      this.closeHandler = () => {
        document.body.classList.remove('wholesale-rec-modal-open');
        this.modalTrigger?.focus();
        this.modalTrigger = null;
      };
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('input', this.inputHandler);
      this.modal?.addEventListener('close', this.closeHandler);
      this.syncExploreDestination(this.exploreContextUrl);
      if (this.dataset.performed === 'true') this.initializeCarousel();
      else this.load(this.dataset.primaryUrl, true);
    }

    async load(url, allowFallback) {
      if (!url) return this.showEmpty();
      this.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
        const html = document.createElement('div');
        html.innerHTML = await response.text();
        const incoming = html.querySelector('wholesale-recommendations');
        const incomingContent = incoming?.querySelector('[data-recommendations-content]');
        if (incomingContent?.querySelector('[data-recommendation-card]')) {
          const exploreUrl = this.exploreContextUrl || incomingContent.dataset.recExploreUrl || '';
          this.content.innerHTML = incomingContent.innerHTML;
          this.syncExploreDestination(exploreUrl);
          this.dataset.performed = 'true';
          this.removeAttribute('aria-busy');
          this.initializeCarousel();
          return;
        }
        if (allowFallback && this.dataset.fallbackUrl && this.dataset.fallbackUrl !== url) {
          await this.load(this.dataset.fallbackUrl, false);
          return;
        }
        this.showEmpty();
      } catch (error) {
        if (allowFallback && this.dataset.fallbackUrl && this.dataset.fallbackUrl !== url) {
          await this.load(this.dataset.fallbackUrl, false);
        } else {
          this.showEmpty();
          console.error(error);
        }
      }
    }

    showEmpty() {
      this.removeAttribute('aria-busy');
      this.classList.add('is-empty');
      this.querySelectorAll('[data-rec-previous], [data-rec-next]').forEach(button => { button.disabled = true; });
      const status = this.querySelector('[data-recommendations-status]');
      if (status) status.textContent = 'No recommendations are currently available.';
    }

    syncExploreDestination(url) {
      const footer = this.querySelector('[data-rec-explore-footer]');
      const link = this.querySelector('[data-rec-explore-link]');
      if (!footer || !link) return;
      if (url) {
        link.href = url;
        footer.hidden = false;
      } else {
        link.removeAttribute('href');
        footer.hidden = true;
      }
    }

    initializeCarousel() {
      this.track = this.querySelector('[data-rec-track]');
      if (!this.track) return;
      this.classList.remove('is-empty');
      this.updateCarousel = () => {
        const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        const pages = this.pageCount();
        const page = maxScroll ? Math.round((this.track.scrollLeft / maxScroll) * (pages - 1)) + 1 : 1;
        const pagination = this.querySelector('[data-rec-pagination]');
        if (pagination) pagination.textContent = `${page} / ${pages}`;
        const previous = this.querySelector('[data-rec-previous]');
        const next = this.querySelector('[data-rec-next]');
        if (previous) previous.disabled = this.track.scrollLeft <= 2;
        if (next) next.disabled = this.track.scrollLeft >= maxScroll - 2;
        const navigation = this.querySelector('[data-rec-navigation]');
        if (navigation) navigation.hidden = pages <= 1;
        this.track.classList.toggle('is-static', pages <= 1);
      };
      this.track.addEventListener('scroll', this.updateCarousel, { passive: true });
      window.addEventListener('resize', this.updateCarousel, { passive: true });
      this.initializeMouseDrag();
      this.updateCarousel();
      this.startAutoplay();
    }

    pageCount() {
      if (!this.track.clientWidth) return 1;
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      return maxScroll <= 2 ? 1 : Math.ceil(maxScroll / this.track.clientWidth) + 1;
    }

    handleClick(event) {
      if (this.suppressCarouselClick && event.target.closest('a')) {
        event.preventDefault();
        event.stopPropagation();
        this.suppressCarouselClick = false;
        return;
      }
      const options = event.target.closest('[data-rec-options]');
      if (options) {
        this.openModal(options.closest('[data-recommendation-card]'), options);
        return;
      }
      if (event.target.closest('[data-rec-modal-close]')) {
        this.closeModal();
        return;
      }
      if (event.target === this.modal) {
        this.closeModal();
        return;
      }
      const modalMinus = event.target.closest('[data-modal-quantity-minus]');
      const modalPlus = event.target.closest('[data-modal-quantity-plus]');
      if (modalMinus || modalPlus) {
        const input = event.target.closest('[data-modal-variant-row]').querySelector('[data-modal-quantity]');
        this.changeQuantity(input, modalPlus ? 1 : -1, 0);
        this.calculateModal();
        return;
      }
      if (event.target.closest('[data-modal-add]')) {
        this.addModalItems();
        return;
      }
      const minus = event.target.closest('[data-rec-quantity-minus]');
      const plus = event.target.closest('[data-rec-quantity-plus]');
      if (minus || plus) {
        const input = event.target.closest('.wholesale-rec-card__quantity').querySelector('input');
        this.changeQuantity(input, plus ? 1 : -1, Number(input.min || 1));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      if (event.target.closest('[data-rec-previous]')) this.scrollByPage(-1);
      if (event.target.closest('[data-rec-next]')) this.scrollByPage(1);
    }

    normalizeQuantity(input, minimum) {
      const maximum = input.max === '' ? Infinity : Math.max(minimum, Number(input.max));
      const value = Math.max(minimum, Math.min(maximum, Math.floor(Number(input.value) || 0)));
      input.value = value;
      return value;
    }

    changeQuantity(input, delta, minimum) {
      input.value = Number(input.value || 0) + delta;
      return this.normalizeQuantity(input, minimum);
    }

    openModal(card, trigger) {
      const template = card?.querySelector('[data-rec-modal-template]');
      if (!template || !this.modal || !this.modalContent) return;
      this.modalTrigger = trigger;
      this.modalContent.replaceChildren(template.content.cloneNode(true));
      this.calculateModal();
      document.body.classList.add('wholesale-rec-modal-open');
      if (typeof this.modal.showModal === 'function') this.modal.showModal();
      else this.modal.setAttribute('open', '');
      requestAnimationFrame(() => this.modal.querySelector('[data-rec-modal-close]')?.focus());
    }

    closeModal() {
      if (!this.modal) return;
      if (typeof this.modal.close === 'function' && this.modal.open) this.modal.close();
      else {
        this.modal.removeAttribute('open');
        this.closeHandler();
      }
    }

    calculateModal() {
      if (!this.modalContent) return;
      let totalItems = 0;
      let subtotal = 0;
      this.modalContent.querySelectorAll('[data-modal-variant-row]').forEach(row => {
        const input = row.querySelector('[data-modal-quantity]');
        const quantity = input.disabled ? 0 : this.normalizeQuantity(input, 0);
        const rowTotal = Number(row.dataset.price || 0) * quantity;
        totalItems += quantity;
        subtotal += rowTotal;
        row.querySelector('[data-modal-row-total]').textContent = formatMoney(rowTotal, this.dataset.moneyFormat);
      });
      this.modalContent.querySelector('[data-modal-total-items]').textContent = totalItems;
      this.modalContent.querySelector('[data-modal-subtotal]').textContent = formatMoney(subtotal, this.dataset.moneyFormat);
      const addButton = this.modalContent.querySelector('[data-modal-add]');
      addButton.disabled = totalItems === 0;
      addButton.setAttribute('aria-disabled', String(totalItems === 0));
    }

    async addModalItems() {
      const addButton = this.modalContent.querySelector('[data-modal-add]');
      const status = this.modalContent.querySelector('[data-modal-status]');
      const items = [...this.modalContent.querySelectorAll('[data-modal-variant-row]')].map(row => ({
        id: row.dataset.variantId,
        quantity: Number(row.querySelector('[data-modal-quantity]').value || 0)
      })).filter(item => item.quantity > 0);
      if (!items.length || addButton.disabled) return;
      const cartDrawer = document.querySelector('cart-drawer');
      const sections = cartDrawer?.getSectionsToRender?.().map(section => section.id) || ['cart-icon-bubble'];
      addButton.disabled = true;
      addButton.setAttribute('aria-busy', 'true');
      status.hidden = true;
      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || 'Unable to add the selected products to the cart.');
        if (cartDrawer?.renderContents && result.sections) {
          this.closeModal();
          cartDrawer.renderContents(result);
        } else {
          this.updateCartIcon(result.sections?.['cart-icon-bubble']);
          status.textContent = 'Selected products added to your cart.';
          status.hidden = false;
          setTimeout(() => this.closeModal(), 700);
        }
      } catch (error) {
        status.textContent = error.message;
        status.hidden = false;
        addButton.disabled = false;
      } finally {
        addButton.removeAttribute('aria-busy');
      }
    }

    updateCartIcon(html) {
      if (!html) return;
      const target = document.getElementById('cart-icon-bubble');
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      if (target && source) target.innerHTML = source.innerHTML;
    }

    scrollByPage(direction) {
      if (!this.track) return;
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const target = Math.max(0, Math.min(maxScroll, this.track.scrollLeft + (direction * this.track.clientWidth)));
      this.track.scrollTo({ left: target, behavior: 'smooth' });
    }

    initializeMouseDrag() {
      this.pointerDownHandler = event => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button, input, select, textarea')) return;
        this.dragState = { pointerId: event.pointerId, startX: event.clientX, startScroll: this.track.scrollLeft, moved: false };
        clearInterval(this.autoplayTimer);
      };
      this.pointerMoveHandler = event => {
        if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;
        const distance = event.clientX - this.dragState.startX;
        if (!this.dragState.moved && Math.abs(distance) < 4) return;
        if (!this.dragState.moved) {
          this.dragState.moved = true;
          this.track.setPointerCapture?.(event.pointerId);
          this.track.classList.add('is-dragging');
        }
        event.preventDefault();
        this.track.scrollLeft = this.dragState.startScroll - distance;
      };
      this.pointerEndHandler = event => {
        if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;
        const moved = this.dragState.moved;
        if (this.track.hasPointerCapture?.(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
        this.track.classList.remove('is-dragging');
        this.dragState = null;
        if (moved) {
          this.suppressCarouselClick = true;
          this.snapToNearestCard();
          setTimeout(() => { this.suppressCarouselClick = false; }, 0);
        }
        this.startAutoplay();
      };
      this.dragStartHandler = event => event.preventDefault();
      this.track.addEventListener('pointerdown', this.pointerDownHandler);
      this.track.addEventListener('pointermove', this.pointerMoveHandler);
      this.track.addEventListener('pointerup', this.pointerEndHandler);
      this.track.addEventListener('pointercancel', this.pointerEndHandler);
      this.track.addEventListener('dragstart', this.dragStartHandler);
    }

    snapToNearestCard() {
      if (!this.track) return;
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const cards = [...this.track.querySelectorAll('[data-recommendation-card]')];
      if (!cards.length || maxScroll <= 2) return;
      const current = this.track.scrollLeft;
      const target = cards.reduce((nearest, card) => {
        const position = Math.max(0, Math.min(maxScroll, card.offsetLeft - this.track.offsetLeft));
        return Math.abs(position - current) < Math.abs(nearest - current) ? position : nearest;
      }, 0);
      this.track.scrollTo({ left: target, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    startAutoplay() {
      clearInterval(this.autoplayTimer);
      if (this.dataset.autoplay !== 'true' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const speed = Math.max(2, Number(this.dataset.autoplaySpeed || 5)) * 1000;
      this.autoplayTimer = setInterval(() => {
        const atEnd = this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 2;
        this.track.scrollTo({ left: atEnd ? 0 : this.track.scrollLeft + this.track.clientWidth, behavior: 'smooth' });
      }, speed);
    }

    disconnectedCallback() {
      clearInterval(this.autoplayTimer);
      if (this.updateCarousel) window.removeEventListener('resize', this.updateCarousel);
      this.track?.removeEventListener('scroll', this.updateCarousel);
      this.track?.removeEventListener('pointerdown', this.pointerDownHandler);
      this.track?.removeEventListener('pointermove', this.pointerMoveHandler);
      this.track?.removeEventListener('pointerup', this.pointerEndHandler);
      this.track?.removeEventListener('pointercancel', this.pointerEndHandler);
      this.track?.removeEventListener('dragstart', this.dragStartHandler);
      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('input', this.inputHandler);
      this.modal?.removeEventListener('close', this.closeHandler);
    }
  }

  if (!customElements.get('wholesale-recommendations')) customElements.define('wholesale-recommendations', WholesaleRecommendations);
})();
